package ai.jessie.web.invoices

import ai.jessie.web.theme.FieldErrorStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldOptionalStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.NumInputStyle
import ai.jessie.web.theme.RowStyle
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.forId
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

// `name` is the server's field name (or the editor's `line.` key), so a returned problem marks
// exactly this control.
internal data class InvoiceField(
    val name: String,
    val label: String,
    val value: String,
    val problem: String? = null,
) {
    val id = invoiceFieldId(name)
}

/** [decimal] inputs keep the exact decimal text and use the number face. */
@Composable
internal fun invoiceInput(
    field: InvoiceField,
    type: InputType<String>,
    attributes: AttrsScope<*>.() -> Unit = {},
    decimal: Boolean = false,
    change: (String) -> Unit,
) {
    invoiceFieldBlock(field) {
        Input(
            type,
            attrs =
                (if (decimal) NumInputStyle else FieldInputStyle).toAttrs {
                    id(field.id)
                    value(field.value)
                    attributes()
                    fieldAttributes(field)
                    onInput { change(it.value) }
                },
        )
    }
}

@Composable
internal fun invoiceSelect(
    field: InvoiceField,
    options: Map<String, String>,
    change: (String) -> Unit,
) {
    invoiceFieldBlock(field) {
        Select(
            attrs =
                FieldInputStyle.toAttrs {
                    id(field.id)
                    fieldAttributes(field)
                    onChange { change(it.value.orEmpty()) }
                }
        ) {
            (mapOf("" to "Bitte wählen") + options).forEach { (value, label) ->
                Option(value, attrs = { if (value == field.value) selected() }) { Text(label) }
            }
        }
    }
}

@Composable
internal fun invoiceCheckbox(id: String, label: String, value: Boolean, change: (Boolean) -> Unit) {
    Div(attrs = RowStyle.toAttrs()) {
        Input(
            InputType.Checkbox,
            attrs = {
                id(id)
                attr("name", id)
                checked(value)
                onChange { change(it.value) }
            },
        )
        Label(attrs = { forId(id) }) { Text(" $label") }
    }
}

// The label text is unchanged; its parenthesis is rendered in the quieter suffix style.
@Composable
internal fun invoiceFieldBlock(field: InvoiceField, control: @Composable () -> Unit) {
    Div(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId(field.id) }) {
            val hint = field.label.indexOf(" (")
            if (hint < 0) Text(field.label)
            else {
                Text(field.label.substring(0, hint + 1))
                Span(attrs = FieldOptionalStyle.toAttrs()) { Text(field.label.substring(hint + 1)) }
            }
        }
        control()
        field.problem?.let { P(attrs = FieldErrorStyle.toAttrs()) { Text(it) } }
    }
}

private fun AttrsScope<*>.fieldAttributes(field: InvoiceField) {
    attr("name", field.name)
    attr("autocomplete", "off")
    if (field.problem != null) {
        attr("aria-invalid", "true")
        attr("aria-describedby", "invoice-problems")
    }
}
